import {Injectable, signal, WritableSignal} from '@angular/core';
import Dexie, {Table} from 'dexie';
import {
  Branch,
  BranchBanner,
  BranchShift,
  ChargeBranch,
  Company, DeliverySlot,
  DiscountBranch,
  Integration,
  Menu,
  Order,
  OrderStatus,
  PaymentMode,
  PrinterConfig,
  Product,
  Role,
  Section, SettingAppType,
  SubCompany,
  SyncAllResponse,
  User,
} from '../../graphql/generated';
import {ExtendedCategory, ExtendedProduct, MenuConfig, OrderMeta, TransFormedSyncAllMenu,} from '../interfaces/interfaces';
import {ExtendedBranch} from '../../pages/main/branch-view/branch-view.component';

class AppDB extends Dexie {
  company!: Table<Company, string>;
  subCompany!: Table<SubCompany, string>;
  branch!: Table<Branch, string>;
  section!: Table<Section, string>;
  menu!: Table<Menu, string>;
  product!: Table<Product, string>;
  role!: Table<Role, string>;
  user!: Table<User, string>;
  order_meta!: Table<OrderMeta, string>;
  payment_mode!: Table<PaymentMode, string>;
  printer_config!: Table<PrinterConfig, string>;
  discount!: Table<DiscountBranch, string>;
  charge!: Table<ChargeBranch, string>;
  integration!: Table<Integration, string>;
  branch_shift!: Table<BranchShift, string>;

  constructor() {
    super('ngdexieliveQuery');

    this.version(106).stores({
      company: 'id',
      subCompany: 'id',
      branch: 'id',
      section: 'id, branch_id',
      menu_config: 'id, branch_id, section_id, default, [branch_id+section_id], [branch_id+default]',
      menu: 'menu_id',
      category: 'composite_id, id, menu_id, listing_sequence',
      product: 'composite_id, id, category_id, sub_category_id, menu_id, [menu_id+category_id], listing_sequence, branch_id, [menu_id+category_id+branch_id]',
      role: 'id',
      user: 'id',
      order: 'id, branch_id, section_id, user_id, update_uuid, sub_section_id',
      order_meta: 'id',
      payment_mode: 'id, name, payment_type',
      printer_config: 'id, bill_template, kot_template, section_id, branch_id, printer_mappings',
      discount: 'id, name, branch_id, section_type',
      charge: 'id, name, branch_id, section_type',
      integration: 'id, name, branch_id, integration_type',
      delivery_slots: 'id, instance_id',
      settings: 'id, setting_app_type, setting_type, name, branch_id',
      branch_shift: 'id, branch_id',
    });
  }
}

const db = new AppDB();

@Injectable({
  providedIn: 'root',
})
export class DatabaseService {
  orders: WritableSignal<Order[]> = signal([] as Order[]);
  subSectionOrders: WritableSignal<{ [key: string]: Order[] }> = signal({});
  sectionOrders: WritableSignal<Order[]> = signal([]);
  constructor() {
  }

  async bulkAddResource(tableName: string, items?: any[]) {
    if (items && items.length > 0) {
      const table = db.table(tableName);
      await table.bulkAdd(items);
    }
  }

  async clearAll() {
    const tableNames = db.tables.map((table) => table.name);
    const promises = tableNames.map((tableName) => db.table(tableName).clear());
    await Promise.all(promises);
  }

  async setOrdersOnSectionSelection(section: Section) {
    const orders = await db
      .table('order')
      .where('section_id')
      .equals(section.id)
      .toArray();
    if (section.sub_sections.length) {
      this.setSubSectionOrders(orders);
    } else {
      this.setSectionOrders(orders);
    }
  }

  async updateOrders(
    initial: boolean = false,
    orders: Order[],
    section: Section,
  ) {
    if (initial) {
      await db.table('order').clear();
    }
    orders.forEach((order) => {
      if (
        order.order_status === OrderStatus.Cancelled ||
        order.order_status === OrderStatus.Completed
      ) {
        db.table('order').delete(order.id);
      } else {
        db.table('order').put(order);
      }
    });

    if (section?.id) {
      await this.setOrdersOnSectionSelection(section);
    }
  }

  updateSubSectionOrder(order: Order, subSectionId: string, remove: boolean) {
    this.subSectionOrders.update((subSections) => {
      if (subSections.hasOwnProperty(subSectionId)) {
        const index = subSections[subSectionId].findIndex(
          (o) => order.id === o.id,
        );
        if (remove) {
          if (index > -1) {
            subSections[subSectionId].splice(index, 1);
          }
        } else {
          const hasActiveItems = order.items && order.items.some((item: any) => !item.is_void);
          const hasKot = !!order.kot_number;
          const isCompleted = order.order_status === 'COMPLETED' || order.order_status === 'CANCELLED';
          const hasInvoice = !!order.invoice_number;
          
          const shouldShow = hasActiveItems || hasKot || isCompleted || hasInvoice;
          
          if (index > -1) {
            if (shouldShow) {
              subSections[subSectionId][index] = order;
            } else {
              subSections[subSectionId].splice(index, 1);
            }
          } else if (shouldShow) {
            subSections[subSectionId].push(order);
          }
        }
      } else {
        if (!remove) {
          const hasActiveItems = order.items && order.items.some((item: any) => !item.is_void);
          const hasKot = !!order.kot_number;
          const isCompleted = order.order_status === 'COMPLETED' || order.order_status === 'CANCELLED';
          const hasInvoice = !!order.invoice_number;
          
          const shouldShow = hasActiveItems || hasKot || isCompleted || hasInvoice;
          
          if (shouldShow) {
            subSections[subSectionId] = [order];
          }
        }
      }
      return subSections;
    });
  }

  async updateSectionOrders(order: Order, remove: boolean = false) {
    this.sectionOrders.update((orders) => {
      const index = orders.findIndex((o) => o.id === order.id);
      if (remove) {
        if (index > -1) {
          orders.splice(index, 1);
        }
      } else {
        const hasActiveItems = order.items && order.items.some((item: any) => !item.is_void);
        const hasKot = !!order.kot_number;
        const isCompleted = order.order_status === 'COMPLETED' || order.order_status === 'CANCELLED';
        const hasInvoice = !!order.invoice_number;
        
        const shouldShow = hasActiveItems || hasKot || isCompleted || hasInvoice;
        
        if (index > -1) {
          if (shouldShow) {
            orders[index] = order;
          } else {
            orders.splice(index, 1);
          }
        } else if (shouldShow) {
          orders.push(order);
        }
      }
      return orders;
    });
  }

  async updateOrder(order: Order, prevSubSectionId?: string) {
    const originalRemove = order.order_status === 'CANCELLED' || order.order_status === 'COMPLETED';
    let actualRemove = originalRemove;

    if (originalRemove) {
      await db.table('order').delete(order.id);
    } else {
      const hasActiveItems = order.items && order.items.some(item => !item.is_void);
      const hasKot = !!order.kot_number;
      const isCompleted = order.order_status === 'COMPLETED' || order.order_status === 'CANCELLED';
      const hasInvoice = !!order.invoice_number;
      
      if (hasActiveItems || hasKot || isCompleted || hasInvoice) {
        await db.table('order').put(order);
      } else {
        await db.table('order').delete(order.id);
        actualRemove = true;
      }
    }

    if (prevSubSectionId) {
      this.updateSubSectionOrder(order, prevSubSectionId, true);
    }
    if (order.sub_section_id) {
      this.updateSubSectionOrder(order, order.sub_section_id, actualRemove);
    } else {
      await this.updateSectionOrders(order, actualRemove);
    }
  }

  async getOrderById(orderId: string): Promise<Order> {
    return db.table('order').get(orderId);
  }

  async saveSyncAllData(syncAllData: SyncAllResponse): Promise<void> {
    const p1 = this.bulkAddResource('company', [syncAllData.company]);
    const p2 = this.bulkAddResource('subCompany', [syncAllData.sub_company]);
    const p3 = this.bulkAddResource('branch', syncAllData.branches);
    const p4 = this.bulkAddResource('section', syncAllData.sections);
    const p5 = this.bulkAddResource('role', syncAllData.roles);
    const p6 = this.bulkAddResource('user', syncAllData.users);
    const p7 = this.bulkAddResource('payment_mode', syncAllData.payment_modes);
    const p8 = this.bulkAddResource('printer_config', syncAllData.printer_configs);
    const p9 = this.bulkAddResource('discount', syncAllData.discounts);
    const p10 = this.bulkAddResource('charge', syncAllData.charges);
    const p11 = this.bulkAddResource('integration', syncAllData.integrations);
    const p12 = this.bulkAddResource('delivery_slots', syncAllData.delivery_slots);
    const p13 = this.bulkAddResource('settings', syncAllData.settings);
    const p14 = this.bulkAddResource('branch_shift', syncAllData.branch_shifts);
    await Promise.all([p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12, p13, p14]);
  }

  async saveSyncAllMenuData(
    syncAllMenuData: TransFormedSyncAllMenu,
  ): Promise<void> {
    const p1 = this.bulkAddResource('menu_config', syncAllMenuData.menuConfig);
    const p2 = this.bulkAddResource('menu', syncAllMenuData.menus);
    const p3 = this.bulkAddResource('category', syncAllMenuData.categories);
    const p4 = this.bulkAddResource('product', syncAllMenuData.products);

    await Promise.all([p1, p2, p3, p4]);
  }

  async getBranches(): Promise<ExtendedBranch[]> {
    return db.table('branch').toArray();
  }

  async getBranchSections(branchId: string): Promise<Section[]> {
    return db
      .table('section')
      .where('branch_id')
      .equals(branchId)
      .sortBy('listing_sequence');
  }

  async getUsers(): Promise<User[]> {
    return db.table('user').toArray();
  }

  async getSubCompany(): Promise<SubCompany[]> {
    return db.table('subCompany').toArray();
  }

  async getUserById(userId: string): Promise<User> {
    return db.table('user').get(userId);
  }

  async getDefaultBranchMenuConfig(branchId: string): Promise<MenuConfig> {
    return db.table('menu_config').get({branch_id: branchId, default: 1});
  }

  async getSectionOrders(sectionId: string) {
    return db.table('order').where('section_id').equals(sectionId).toArray();
  }

  async getSettings() {
    return db.table('settings').where('setting_app_type').equals(SettingAppType.Billing).toArray();
  }

  async getBranchSectionMenuConfig(
    branchId: string,
    sectionId: string,
  ): Promise<MenuConfig> {
    return db
      .table('menu_config')
      .get({branch_id: branchId, section_id: sectionId});
  }

  async getProductByCategoryIdMenuId(
    menuId: string | undefined,
    categoryId: string,
  ): Promise<ExtendedProduct[]> {
    if (!menuId) return Promise.resolve([]);

    return db
      .table('product')
      .where({menu_id: menuId, category_id: categoryId})
      .sortBy('listing_sequence');
  }

  async getMenuCategories(menuId: string): Promise<ExtendedCategory[]> {
    return db
      .table('category')
      .where('menu_id')
      .equals(menuId)
      .toArray();
  }

  async getMenuProducts(
    menuId: string | undefined,
    branchId: string,
  ): Promise<ExtendedProduct[]> {
    if (!menuId || !branchId) return Promise.resolve([]);
    return db
      .table('product')
      .where({menu_id: menuId, branch_id: branchId})
      .toArray();
  }

  async getMenuProductById(menuId: string, productId: string): Promise<ExtendedProduct | undefined> {
    return db
      .table('product')
      .where({menu_id: menuId, id: productId})
      .first();
  }

  async getProductById(productId: string): Promise<Product> {
    return db.table('product')
      .where({id: productId})
      .first();
  }

  async getCategoryByMenuId(menuId: string): Promise<ExtendedCategory[]> {
    return db
      .table('category')
      .where('menu_id')
      .equals(menuId)
      .sortBy('listing_sequence');
  }

  async getPaymentModes(): Promise<PaymentMode[]> {
    return db.table('payment_mode').toArray();
  }

  async getDeliverySlots(): Promise<DeliverySlot[]> {
    return db.table('delivery_slots').toArray();
  }

  async deleteFromDatabase(tableName: string, key: string) {
    await db.table(tableName).delete(key);
  }

  async getSectionById(sectionId: string): Promise<Section> {
    return db.table('section').get(sectionId);
  }

  async getBranchById(branchId: string): Promise<Branch> {
    return db.table('branch').get(branchId);
  }

  async getPrinterConfigs(): Promise<PrinterConfig[]> {
    return db.table('printer_config').toArray();
  }

  async getSectionPrinterConfig(sectionId: string): Promise<PrinterConfig> {
    return db.table('printer_config').where('section_id').equals(sectionId).first();
  }

  async getBranchPrinterConfig(branchId: string): Promise<PrinterConfig | undefined> {
    return db
      .table('printer_config')
      .where('branch_id')
      .equals(branchId)
      .and((config) => config.section_id === null)
      .first();
  }

  async getDiscountBranch(): Promise<DiscountBranch[]> {
    return db.table('discount').toArray();
  }

  async getChargeBranches(): Promise<ChargeBranch[]> {
    return db.table('charge').toArray();
  }

  async getDiscountsBranchByBranchId(
    branchId: string,
  ): Promise<DiscountBranch[]> {
    return db.table('discount').where('branch_id').equals(branchId).toArray();
  }

  async getChargeBranchByBranchId(branchId: string): Promise<ChargeBranch[]> {
    return db.table('charge').where('branch_id').equals(branchId).toArray();
  }

  async getIntegrationByBranchId(branchId: string) {
    return db
      .table('integration')
      .where('branch_id')
      .equals(branchId)
      .toArray();
  }

  async getOrderMetaById(orderId: string): Promise<OrderMeta | undefined> {
    return db.table('order_meta').get(orderId);
  }

  async getIntegrationByName(name: string): Promise<any> {
    return db.table('integration').where('name').equals(name).first();
  }

  async getProductByMenuId(
    menuId: string | undefined,
    productId: string,
  ): Promise<ExtendedProduct> {
    return db.table('product').get({menu_id: menuId, id: productId});
  }

  async getBranchBanners(branchId: string): Promise<BranchBanner[]> {
    const branch = await this.getBranchById(branchId);
    return branch?.banners || [];
  }

  updateBranch(branch: ExtendedBranch) {
    return db.table('branch').put(branch);
  }

  async updateOrderMeta(orderId: string, orderMeta: OrderMeta) {
    return db.table('order_meta').update(orderId, orderMeta);
  }

  async createOrderMeta(orderMeta: OrderMeta) {
    return db.table('order_meta').put(orderMeta);
  }

  async getBranchUsers(branchId: string): Promise<User[]> {
    const users = await db.table('user').toArray();

    return users.filter((user) => {
      const branches = user.branches || [];
      const branchIds = branches.map((branch: Branch) => branch.id);
      return branchIds.includes(branchId);
    })
  }

  async getBranchShifts(): Promise<BranchShift[]> {
    return await db.table('branch_shift').toArray();
  }

  async getBranchShiftsByBranchId(branchId: string): Promise<BranchShift[]> {
    return await db.table('branch_shift').where('branch_id').equals(branchId).toArray();
  }

  async getBranchShift(branchId: string): Promise<BranchShift> {
    return db.table('branch_shift').where('branch_id').equals(branchId).first();
  }

  async addBranchShifts(branchShifts: BranchShift[]) {
    await db.table('branch_shift').bulkAdd(branchShifts);
  }

  async deleteAllBranchShifts() {
    await db.table('branch_shift').clear();
  }

  private setSubSectionOrders(orders: Array<any>) {
    const subSectionOrders: { [k: string]: Order[] } = {};
    orders
      .filter((o) => o.sub_section_id && !o.is_advance_order)
      .forEach((o) => {
        const hasActiveItems = o.items && o.items.some((item: any) => !item.is_void);
        const hasKot = !!o.kot_number;
        const isCompleted = o.order_status === 'COMPLETED' || o.order_status === 'CANCELLED';
        const hasInvoice = !!o.invoice_number;
        
        if (!hasActiveItems && !hasKot && !isCompleted && !hasInvoice) {
          return;
        }
        if (subSectionOrders.hasOwnProperty(o.sub_section_id)) {
          subSectionOrders[o.sub_section_id].push(o);
        } else {
          subSectionOrders[o.sub_section_id] = [o];
        }
      });
    this.subSectionOrders.set(subSectionOrders);
    this.sectionOrders.set([]);
  }

  private setSectionOrders(orders: Array<any>) {
    const nonAdvanceOrders = orders.filter((o) => {
      if (o.is_advance_order) return false;
      
      const hasActiveItems = o.items && o.items.some((item: any) => !item.is_void);
      const hasKot = !!o.kot_number;
      const isCompleted = o.order_status === 'COMPLETED' || o.order_status === 'CANCELLED';
      const hasInvoice = !!o.invoice_number;
      
      if (!hasActiveItems && !hasKot && !isCompleted && !hasInvoice) {
        return false;
      }
      
      return true;
    });
    
    this.subSectionOrders.set({});
    this.sectionOrders.set(nonAdvanceOrders);
  }

  async getSettingsByBranchId(branchId: string) {
    const settings =  await db.table('settings').where('branch_id').equals(branchId).toArray();
    return settings.filter((setting) => setting.setting_app_type === SettingAppType.Billing);
  }

  async cleanupIncompleteDraftOrders() {
    try {
      const allOrders = await db.table('order').toArray();
      const incompleteDrafts = allOrders.filter((order) => {
        const hasKot = !!order.kot_number;
        const hasItems = order.items && order.items.length > 0;
        const hasActiveItems = order.items && order.items.some((item: any) => !item.is_void);
        const isCompleted = order.order_status === 'COMPLETED' || order.order_status === 'CANCELLED';
        const hasInvoice = !!order.invoice_number;

        const isEmpty = !hasActiveItems && !hasKot && !isCompleted && !hasInvoice;
        
        return isEmpty;
      });
      
      for (const order of incompleteDrafts) {
        await db.table('order').delete(order.id);
        await db.table('order_meta').delete(order.id);
        console.log(`Cleaned up incomplete draft order: ${order.id}`);
      }
      
      return incompleteDrafts.length;
    } catch (error) {
      console.error('Error cleaning up incomplete draft orders:', error);
      return 0;
    }
  }
}
